import type { ThemeConfig, ThemeConfigAPI } from './types'
import { defaultThemeConfig } from './defaults'

const STORAGE_KEY = 'docurious-theme-config'
const STORAGE_VERSION = '5'
const VERSION_KEY = 'docurious-theme-version'

/**
 * Mock API for theme configuration.
 * Uses localStorage for persistence during development.
 * Replace these functions with real API calls when backend is ready.
 */

export const themeApi: ThemeConfigAPI = {
  async getTheme(): Promise<ThemeConfig> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 50))

    try {
      const version = localStorage.getItem(VERSION_KEY)
      if (version !== STORAGE_VERSION) {
        // Version mismatch, clear old config
        localStorage.removeItem(STORAGE_KEY)
        localStorage.setItem(VERSION_KEY, STORAGE_VERSION)
        return defaultThemeConfig
      }

      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) {
        return defaultThemeConfig
      }

      const parsed = JSON.parse(stored) as Partial<ThemeConfig>
      // Merge with defaults to ensure all keys exist
      return mergeWithDefaults(parsed)
    } catch {
      console.warn('Failed to load theme config, using defaults')
      return defaultThemeConfig
    }
  },

  async saveTheme(config: ThemeConfig): Promise<void> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100))

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
      localStorage.setItem(VERSION_KEY, STORAGE_VERSION)
    } catch (error) {
      console.error('Failed to save theme config:', error)
      throw new Error('Failed to save theme configuration')
    }
  },

  async resetTheme(): Promise<void> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 50))

    localStorage.removeItem(STORAGE_KEY)
  },
}

function mergeWithDefaults(partial: Partial<ThemeConfig>): ThemeConfig {
  return {
    colors: { ...defaultThemeConfig.colors, ...partial.colors },
    typography: { ...defaultThemeConfig.typography, ...partial.typography },
    spacing: { ...defaultThemeConfig.spacing, ...partial.spacing },
    branding: { ...defaultThemeConfig.branding, ...partial.branding },
    style: { ...defaultThemeConfig.style, ...partial.style },
    layout: { ...defaultThemeConfig.layout, ...partial.layout },
  }
}
